/**
 * 🎨 UNIVERSAL GALLERY ELEMENT 2025
 * 
 * Gallery Element mit verschiedenen Layout-Modi:
 * - Masonry Layout
 * - Grid Layout
 * - Floating Layout
 * - Carousel
 * - Justified Layout
 * 
 * Features:
 * - Image Upload mit Captions
 * - Library Integration
 * - Lightbox
 * - Hover Effects
 * - Responsive Settings
 */

import React, { useState, useEffect, useRef } from 'react';
import Masonry from 'react-masonry-css';
import GalleryFloatingToolbar from './GalleryFloatingToolbar';
import './UniversalGalleryElement.css';

function UniversalGalleryElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    colors = {},
    currentlyEditingElementId,
    onEditingChange,
    fonts = {} // ✅ NEW: Brand Fonts (globalBigFont, globalSmallFont)
}) {
    const [isToolbarVisible, setIsToolbarVisible] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const galleryRef = useRef(null);
    const carouselRef = useRef(null);
    
    // Check if this element is currently being edited
    const isEditing = currentlyEditingElementId === element.id;
    
    // Sync toolbar visibility with editing state
    useEffect(() => {
        if (isEditing) {
            setIsToolbarVisible(true);
        } else {
            setIsToolbarVisible(false);
        }
    }, [isEditing]);
    
    const handleEdit = () => {
        if (!isEditMode) return;
        
        if (isEditing) {
            // Close toolbar
            onEditingChange(null);
            setIsToolbarVisible(false);
        } else {
            // Open toolbar
            onEditingChange(element.id);
            setIsToolbarVisible(true);
        }
    };
    
    const handleStyleChange = (updates) => {
        onUpdate({ ...element, ...updates });
    };
    
    // Gallery Items (sortiert nach order)
    const galleryItems = (element.galleryItems || [])
        .sort((a, b) => a.order - b.order);
    
    // ✅ FIXED: Responsive Properties - 0 Werte müssen funktionieren!
    const columns = isMobileLayout 
        ? (typeof element.galleryColumnsMobile === 'number' ? element.galleryColumnsMobile : 2) 
        : (typeof element.galleryColumns === 'number' ? element.galleryColumns : 3);
    
    const gap = isMobileLayout
        ? (typeof element.galleryGapMobile === 'number' ? element.galleryGapMobile : 15)
        : (typeof element.galleryGap === 'number' ? element.galleryGap : 20);
    
    const borderRadius = isMobileLayout
        ? (typeof element.galleryItemBorderRadiusMobile === 'number' ? element.galleryItemBorderRadiusMobile : 12)
        : (typeof element.galleryItemBorderRadius === 'number' ? element.galleryItemBorderRadius : 12);
    
    const captionFontSize = isMobileLayout
        ? (typeof element.galleryCaptionFontSizeMobile === 'number' ? element.galleryCaptionFontSizeMobile : 12)
        : (typeof element.galleryCaptionFontSize === 'number' ? element.galleryCaptionFontSize : 14);
    
    const titleSize = isMobileLayout
        ? (typeof element.galleryTitleSizeMobile === 'number' ? element.galleryTitleSizeMobile : 16)
        : (typeof element.galleryTitleSize === 'number' ? element.galleryTitleSize : 18);
    
    const subtitleSize = isMobileLayout
        ? (typeof element.gallerySubtitleSizeMobile === 'number' ? element.gallerySubtitleSizeMobile : 12)
        : (typeof element.gallerySubtitleSize === 'number' ? element.gallerySubtitleSize : 14);
    
    const itemHeight = isMobileLayout
        ? (typeof element.galleryItemHeightMobile === 'number' ? element.galleryItemHeightMobile : 300)
        : (typeof element.galleryItemHeight === 'number' ? element.galleryItemHeight : 400);
    
    const itemMaxWidth = isMobileLayout
        ? (typeof element.galleryItemMaxWidthMobile === 'number' ? element.galleryItemMaxWidthMobile : 350)
        : (typeof element.galleryItemMaxWidth === 'number' ? element.galleryItemMaxWidth : 500);
    
    // ✅ RESPONSIVE LAYOUT MODE
    const layoutMode = isMobileLayout
        ? (element.galleryLayoutMobile || element.galleryLayout || 'carousel')
        : (element.galleryLayout || 'carousel');
    
    console.log('🎨 Gallery Responsive Settings:', {
        layoutMode,
        isMobileLayout,
        columns,
        gap,
        itemHeight,
        itemMaxWidth,
        elementData: {
            galleryLayout: element.galleryLayout,
            galleryLayoutMobile: element.galleryLayoutMobile,
            galleryColumns: element.galleryColumns,
            galleryColumnsMobile: element.galleryColumnsMobile
        }
    });
    
    // Margin Styles (Responsive)
    const marginStyles = {
        marginTop: isMobileLayout 
            ? `${element.marginTopMobile || element.marginTop || 20}px`
            : `${element.marginTop || 20}px`,
        marginBottom: isMobileLayout
            ? `${element.marginBottomMobile || element.marginBottom || 20}px`
            : `${element.marginBottom || 20}px`,
        marginLeft: isMobileLayout
            ? `${element.marginLeftMobile || element.marginLeft || 0}px`
            : `${element.marginLeft || 0}px`,
        marginRight: isMobileLayout
            ? `${element.marginRightMobile || element.marginRight || 0}px`
            : `${element.marginRight || 0}px`
    };
    
    // ✅ CAROUSEL LAYOUT STYLES
    const getCarouselLayoutStyles = () => {
        const validGap = Math.max(0, Number(gap) || 20);
        const edgeMargin = isMobileLayout
            ? (element.galleryEdgeMarginMobile !== undefined ? element.galleryEdgeMarginMobile : (element.galleryEdgeMargin || 0))
            : (element.galleryEdgeMargin || 0);
        
        return {
            display: 'flex',
            gap: `${validGap}px`,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
            boxSizing: 'border-box',
            alignItems: 'stretch',
            marginLeft: `${edgeMargin}px` // ✅ EINFACH: Linker Margin
        };
    };
    
    // ✅ MASONRY BREAKPOINTS (für react-masonry-css)
    const getMasonryBreakpoints = () => {
        const validColumns = Math.max(1, Math.min(6, Number(columns) || 3));
        
        // Responsive breakpoints basierend auf columns setting
        return {
            default: validColumns,
            1100: Math.max(1, validColumns - 1),
            700: Math.max(1, validColumns - 2),
            500: 1
        };
    };
    
    // Handle Image Load (kept for future use if needed)
    const handleImageLoad = (itemId, event) => {
        // Currently not used, but kept for potential future features
    };
    
    // ✅ ITEM STYLES - Carousel vs Grid Masonry
    const getItemStyles = (item, index) => {
        const validColumns = Math.max(1, Math.min(6, Number(columns) || 3));
        const validGap = Math.max(0, Number(gap) || 20);
        const validHeight = Math.max(200, Math.min(600, Number(itemHeight) || 400));
        const validMaxWidth = Math.max(200, Math.min(1200, Number(itemMaxWidth) || 500));
        
        console.log('🎨 Gallery Item Style:', {
            mode: layoutMode,
            index,
            validColumns,
            validGap,
            validHeight
        });
        
        // ✅ BASE STYLES (für beide Modi)
        const baseStyles = {
            borderRadius: `${borderRadius}px`,
            overflow: 'hidden',
            position: 'relative',
            cursor: element.galleryLightboxEnabled ? 'pointer' : 'default',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
        };
        
        // ✅ CAROUSEL MODE - Flex mit Peek Effect
        if (layoutMode === 'carousel') {
            let itemWidth;
            const peekAmount = validGap * 2; // Peek zeigt 2x Gap vom nächsten Bild
            
            if (validColumns === 1) {
                // Bei 1 Column: 95% Breite für Peek
                itemWidth = `calc(95% - ${peekAmount}px)`;
            } else if (validColumns === 2) {
                // Bei 2 Columns: etwas schmaler für Peek
                itemWidth = `calc(48% - ${validGap / 2}px)`;
            } else if (validColumns === 3) {
                // Bei 3+ Columns: normale Berechnung aber etwas schmaler
                itemWidth = `calc(32% - ${(validGap * 2) / 3}px)`;
            } else if (validColumns === 4) {
                itemWidth = `calc(24% - ${(validGap * 3) / 4}px)`;
            } else if (validColumns === 5) {
                itemWidth = `calc(19% - ${(validGap * 4) / 5}px)`;
            } else { // 6 columns
                itemWidth = `calc(16% - ${(validGap * 5) / 6}px)`;
            }
            
            return {
                ...baseStyles,
                flex: `0 0 ${itemWidth}`,
                minWidth: isMobileLayout ? '180px' : '220px',
                maxWidth: `${validMaxWidth}px`,
                scrollSnapAlign: 'start',
                height: `${validHeight}px`
                // ✅ Kein margin auf Items - padding-left/right am Container sorgt für Spalten
            };
        }
        
        // ✅ GRID MASONRY MODE - Grid Item (natürliche Höhe basierend auf Bild)
        if (layoutMode === 'masonry') {
            return {
                ...baseStyles,
                width: '100%',
                height: 'auto', // Auto height für Masonry
                minHeight: 'unset', // ✅ Keine minimale Höhe
                aspectRatio: 'auto' // ✅ Natürliches Aspect Ratio des Bildes
            };
        }
        
        return baseStyles;
    };
    
    // Hover Effect Class
    const getHoverEffectClass = () => {
        const effect = element.galleryHoverEffect || 'zoom';
        return `gallery-item-hover-${effect}`;
    };
    
    // Handle Lightbox
    const handleImageClick = (index) => {
        if (element.galleryLightboxEnabled && !isEditMode) {
            setSelectedImageIndex(index);
        }
    };
    
    const closeLightbox = () => {
        setSelectedImageIndex(null);
    };
    
    // Caption Position Class
    const getCaptionClass = () => {
        const position = element.galleryCaptionPosition || 'overlay-bottom';
        return `gallery-caption-${position}`;
    };
    
    // Text Display Mode Class
    const getTextDisplayClass = () => {
        const mode = element.galleryTextDisplayMode || 'gradient-bottom';
        return `gallery-text-display-${mode}`;
    };
    
    // Text Position Class (für backwards compatibility)
    const getTextPositionClass = () => {
        const mode = element.galleryTextDisplayMode || 'gradient-bottom';
        // Map modes to positions
        if (mode === 'below-image' || mode === 'card-below') return 'gallery-text-below';
        return 'gallery-text-overlay';
    };
    
    // ✅ CAROUSEL NAVIGATION mit Infinite Loop
    const scrollCarousel = (direction) => {
        if (!carouselRef.current) return;
        
        const carousel = carouselRef.current;
        const scrollAmount = carousel.offsetWidth * 0.8; // Scroll 80% of width
        const currentScroll = carousel.scrollLeft;
        const maxScroll = carousel.scrollWidth - carousel.offsetWidth;
        
        if (direction === 'left') {
            // ✅ Infinite Loop: Wenn am Anfang, springe zum Ende
            if (currentScroll <= 5) { // Threshold von 5px für smooth transition
                carousel.scrollTo({
                    left: maxScroll,
                    behavior: 'smooth'
                });
            } else {
                carousel.scrollTo({
                left: currentScroll - scrollAmount,
                behavior: 'smooth'
            });
            }
        } else {
            // ✅ Infinite Loop: Wenn am Ende, springe zum Anfang
            if (currentScroll >= maxScroll - 5) { // Threshold von 5px
                carousel.scrollTo({
                    left: 0,
                    behavior: 'smooth'
                });
            } else {
                carousel.scrollTo({
                left: currentScroll + scrollAmount,
                behavior: 'smooth'
            });
            }
        }
    };
    
    // Animation Delay
    const getAnimationDelay = (index) => {
        if (!element.galleryAnimationsEnabled) return 0;
        const delay = typeof element.galleryAnimationDelay === 'number' ? element.galleryAnimationDelay : 100;
        return index * delay; // ✅ 0 delay funktioniert jetzt!
    };
    
    return (
        <>
            <div 
                ref={galleryRef}
                className={`universal-gallery-element ${isEditMode ? 'edit-mode' : ''} ${isEditing ? 'editing' : ''}`}
                style={{
                    ...marginStyles,
                    '--gallery-gap': `${gap}px` // CSS Variable für Masonry Gap
                }}
                onClick={isEditMode ? handleEdit : undefined}
            >
                {/* Empty State - wenn keine Bilder */}
                {galleryItems.length === 0 && isEditMode && (
                    <div className="gallery-empty-state">
                        <i className="fas fa-images"></i>
                        <p>Click to add images to your gallery</p>
                    </div>
                )}
                
                {/* Gallery Items */}
                {galleryItems.length > 0 && (
                    <div className="gallery-container" style={{ position: 'relative' }}>
                        {/* ✅ CAROUSEL LAYOUT */}
                        {layoutMode === 'carousel' && (
                        <div 
                            ref={carouselRef}
                            className={`gallery-layout gallery-layout-carousel ${getTextDisplayClass()}`}
                                style={getCarouselLayoutStyles()}
                            >
                            {galleryItems.map((item, index) => (
                                <div
                                    key={item.id}
                                    className={`gallery-item ${getHoverEffectClass()} ${element.galleryAnimationsEnabled ? 'animated' : ''}`}
                                    style={{
                                        ...getItemStyles(item, index),
                                        animationDelay: `${getAnimationDelay(index)}ms`
                                    }}
                                    onClick={() => handleImageClick(index)}
                                >
                                    {/* Image */}
                                    <img
                                        src={item.imageUrl || '/img/placeholder.svg'}
                                        alt={item.alt || item.title || item.caption || 'Gallery Image'}
                                        className="gallery-item-image"
                                        style={{
                                            objectFit: element.galleryImageFit || 'cover'
                                        }}
                                    />
                                    
                                    {/* Text Content (wenn aktiviert) */}
                                    {(element.galleryShowTitle || element.galleryShowSubtitle || element.galleryShowDescription || element.galleryShowCaption) && (
                                        <div 
                                            className={`gallery-item-text ${getTextPositionClass()} ${getTextDisplayClass()}`}
                                            style={{
                                                color: element.galleryTextColor || '#ffffff',
                                                // ✅ Dynamic Background Color mit Opacity (nur für Overlay/Gradient Modes)
                                                ...(element.galleryTextDisplayMode === 'gradient-bottom' && {
                                                    background: element.galleryTextBackgroundColor 
                                                        ? `linear-gradient(to top, ${element.galleryTextBackgroundColor}${Math.round((element.galleryTextBackgroundOpacity ?? 95) * 2.55).toString(16).padStart(2, '0')} 0%, ${element.galleryTextBackgroundColor}${Math.round((element.galleryTextBackgroundOpacity ?? 70) * 1.78).toString(16).padStart(2, '0')} 50%, transparent 100%)`
                                                        : undefined
                                                }),
                                                ...(element.galleryTextDisplayMode === 'overlay-dark' && {
                                                    background: element.galleryTextBackgroundColor 
                                                        ? `${element.galleryTextBackgroundColor}${Math.round((element.galleryTextBackgroundOpacity ?? 75) * 2.55).toString(16).padStart(2, '0')}`
                                                        : undefined
                                                }),
                                                ...(element.galleryTextDisplayMode === 'card-below' && {
                                                    background: element.galleryTextBackgroundColor || undefined
                                                })
                                            }}
                                        >
                                            {/* Title */}
                                            {element.galleryShowTitle !== false && item.title && (
                                                <div 
                                                    className="gallery-item-title"
                                                    style={{
                                                        fontSize: `${titleSize}px`,
                                                        fontWeight: element.galleryTitleWeight || '700',
                                                        marginBottom: '4px',
                                                        fontFamily: element.galleryTitleFontFamily || undefined
                                                    }}
                                                >
                                                    {item.title}
                                                </div>
                                            )}
                                            
                                            {/* Subtitle */}
                                            {element.galleryShowSubtitle !== false && item.subtitle && (
                                                <div 
                                                    className="gallery-item-subtitle"
                                                    style={{
                                                        fontSize: `${subtitleSize}px`,
                                                        fontWeight: element.gallerySubtitleWeight || '600',
                                                        marginBottom: '4px',
                                                        opacity: 0.9,
                                                        fontFamily: element.galleryTextFontFamily || undefined
                                                    }}
                                                >
                                                    {item.subtitle}
                                                </div>
                                            )}
                                            
                                            {/* Description */}
                                            {element.galleryShowDescription !== false && item.description && (
                                                <div 
                                                    className="gallery-item-description"
                                                    style={{
                                                        fontSize: `${captionFontSize}px`,
                                                        fontWeight: '400',
                                                        marginBottom: '4px',
                                                        opacity: 0.85,
                                                        lineHeight: 1.4,
                                                        fontFamily: element.galleryTextFontFamily || undefined
                                                    }}
                                                >
                                                    {item.description}
                                                </div>
                                            )}
                                            
                                            {/* Caption */}
                                            {element.galleryShowCaption !== false && item.caption && (
                                                <div 
                                                    className="gallery-item-caption-text"
                                                    style={{
                                                        fontSize: `${captionFontSize - 1}px`,
                                                        fontWeight: '400',
                                                        opacity: 0.75,
                                                        fontStyle: 'italic',
                                                        fontFamily: element.galleryTextFontFamily || undefined
                                                    }}
                                                >
                                                    {item.caption}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Link Indicator */}
                                    {item.link && (
                                        <div className="gallery-item-link-indicator">
                                            <i className="fas fa-external-link-alt"></i>
                                        </div>
                                    )}
                                </div>
                            ))}
                            </div>
                        )}
                        
                        {/* ✅ MASONRY LAYOUT mit react-masonry-css */}
                        {layoutMode === 'masonry' && (
                            <Masonry
                                breakpointCols={getMasonryBreakpoints()}
                                className={`gallery-masonry ${getTextDisplayClass()}`}
                                columnClassName="gallery-masonry-column"
                        >
                        {galleryItems.map((item, index) => (
                            <div
                                key={item.id}
                                className={`gallery-item ${getHoverEffectClass()} ${element.galleryAnimationsEnabled ? 'animated' : ''}`}
                                style={{
                                    ...getItemStyles(item, index),
                                    animationDelay: `${getAnimationDelay(index)}ms`
                                }}
                                onClick={() => handleImageClick(index)}
                            >
                                {/* Image */}
                                <img
                                    src={item.imageUrl || '/img/placeholder.svg'}
                                    alt={item.alt || item.title || item.caption || 'Gallery Image'}
                                    className="gallery-item-image"
                                    style={{
                                        objectFit: element.galleryImageFit || 'cover'
                                    }}
                                />
                                
                                {/* Text Content (wenn aktiviert) */}
                                {(element.galleryShowTitle || element.galleryShowSubtitle || element.galleryShowDescription || element.galleryShowCaption) && (
                                    <div 
                                        className={`gallery-item-text ${getTextPositionClass()} ${getTextDisplayClass()}`}
                                        style={{
                                            color: element.galleryTextColor || '#ffffff',
                                            // ✅ Dynamic Background Color mit Opacity (nur für Overlay/Gradient Modes)
                                            ...(element.galleryTextDisplayMode === 'gradient-bottom' && {
                                                background: element.galleryTextBackgroundColor 
                                                    ? `linear-gradient(to top, ${element.galleryTextBackgroundColor}${Math.round((element.galleryTextBackgroundOpacity ?? 95) * 2.55).toString(16).padStart(2, '0')} 0%, ${element.galleryTextBackgroundColor}${Math.round((element.galleryTextBackgroundOpacity ?? 70) * 1.78).toString(16).padStart(2, '0')} 50%, transparent 100%)`
                                                    : undefined
                                            }),
                                            ...(element.galleryTextDisplayMode === 'overlay-dark' && {
                                                background: element.galleryTextBackgroundColor 
                                                    ? `${element.galleryTextBackgroundColor}${Math.round((element.galleryTextBackgroundOpacity ?? 75) * 2.55).toString(16).padStart(2, '0')}`
                                                    : undefined
                                            }),
                                            ...(element.galleryTextDisplayMode === 'card-below' && {
                                                background: element.galleryTextBackgroundColor || undefined
                                            })
                                        }}
                                    >
                                        {/* Title */}
                                        {element.galleryShowTitle !== false && item.title && (
                                            <div 
                                                className="gallery-item-title"
                                                style={{
                                                    fontSize: `${titleSize}px`,
                                                    fontWeight: element.galleryTitleWeight || '700',
                                                    marginBottom: '4px',
                                                    fontFamily: element.galleryTitleFontFamily || undefined
                                                }}
                                            >
                                                {item.title}
                                            </div>
                                        )}
                                        
                                        {/* Subtitle */}
                                        {element.galleryShowSubtitle !== false && item.subtitle && (
                                            <div 
                                                className="gallery-item-subtitle"
                                                style={{
                                                    fontSize: `${subtitleSize}px`,
                                                    fontWeight: element.gallerySubtitleWeight || '600',
                                                    marginBottom: '4px',
                                                    opacity: 0.9,
                                                    fontFamily: element.galleryTextFontFamily || undefined
                                                }}
                                            >
                                                {item.subtitle}
                                            </div>
                                        )}
                                        
                                        {/* Description */}
                                        {element.galleryShowDescription !== false && item.description && (
                                            <div 
                                                className="gallery-item-description"
                                                style={{
                                                    fontSize: `${captionFontSize}px`,
                                                    fontWeight: '400',
                                                    marginBottom: '4px',
                                                    opacity: 0.85,
                                                    lineHeight: 1.4,
                                                    fontFamily: element.galleryTextFontFamily || undefined
                                                }}
                                            >
                                                {item.description}
                                            </div>
                                        )}
                                        
                                        {/* Caption */}
                                        {element.galleryShowCaption !== false && item.caption && (
                                            <div 
                                                className="gallery-item-caption-text"
                                                style={{
                                                    fontSize: `${captionFontSize - 1}px`,
                                                    fontWeight: '400',
                                                    opacity: 0.75,
                                                    fontStyle: 'italic',
                                                    fontFamily: element.galleryTextFontFamily || undefined
                                                }}
                                            >
                                                {item.caption}
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {/* Link Indicator */}
                                {item.link && (
                                    <div className="gallery-item-link-indicator">
                                        <i className="fas fa-external-link-alt"></i>
                                    </div>
                                )}
                            </div>
                        ))}
                            </Masonry>
                        )}
                        
                        {/* Carousel Navigation Buttons - nur im Carousel Mode */}
                        {layoutMode === 'carousel' && element.galleryShowCarouselNav !== false && galleryItems.length > 1 && (
                            <>
                                <button
                                    className={`gallery-carousel-nav gallery-carousel-prev ${
                                        element.galleryNavStyle === 'dark' ? 'nav-dark' : 'nav-light'
                                    }`}
                                    style={{
                                        borderRadius: `${typeof element.galleryNavBorderRadius === 'number' ? element.galleryNavBorderRadius : 0}px`
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        scrollCarousel('left');
                                    }}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                <button
                                    className={`gallery-carousel-nav gallery-carousel-next ${
                                        element.galleryNavStyle === 'dark' ? 'nav-dark' : 'nav-light'
                                    }`}
                                    style={{
                                        borderRadius: `${typeof element.galleryNavBorderRadius === 'number' ? element.galleryNavBorderRadius : 0}px`
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        scrollCarousel('right');
                                    }}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
            
            {/* Floating Toolbar */}
            {isEditMode && (
                <GalleryFloatingToolbar
                    isEditMode={isEditMode}
                    isVisible={isToolbarVisible}
                    element={element}
                    onStyleChange={handleStyleChange}
                    onClose={() => {
                        onEditingChange(null);
                        setIsToolbarVisible(false);
                    }}
                    colors={colors}
                    isMobileLayout={isMobileLayout}
                    galleryRef={galleryRef}
                    brandFonts={fonts} // ✅ NEW: Brand Fonts für Font Family Section
                />
            )}
            
            {/* Lightbox */}
            {selectedImageIndex !== null && element.galleryLightboxEnabled && (
                <div className="gallery-lightbox" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>
                        <i className="fas fa-times"></i>
                    </button>
                    
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={galleryItems[selectedImageIndex].imageUrl}
                            alt={galleryItems[selectedImageIndex].alt || galleryItems[selectedImageIndex].caption}
                        />
                        
                        {galleryItems[selectedImageIndex].caption && (
                            <div className="lightbox-caption">
                                {galleryItems[selectedImageIndex].caption}
                            </div>
                        )}
                        
                        {/* Navigation */}
                        {galleryItems.length > 1 && (
                            <>
                                <button
                                    className="lightbox-nav lightbox-prev"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedImageIndex(selectedImageIndex === 0 ? galleryItems.length - 1 : selectedImageIndex - 1);
                                    }}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                <button
                                    className="lightbox-nav lightbox-next"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedImageIndex(selectedImageIndex === galleryItems.length - 1 ? 0 : selectedImageIndex + 1);
                                    }}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </>
                        )}
                        
                        {/* Counter */}
                        <div className="lightbox-counter">
                            {selectedImageIndex + 1} / {galleryItems.length}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default UniversalGalleryElement;

